import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { menuService, type MenuItem } from '@/api/services/menu.service';
import { useAuth } from '@/context/AuthContext';

interface MenuContextType {
  menuItems: MenuItem[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const MenuContext = createContext<MenuContextType | undefined>(undefined);

interface MenuProviderProps {
  children: ReactNode;
}

export const MenuProvider: React.FC<MenuProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasFetchedRef = React.useRef(false);

  const fetchMenu = async () => {
    // Prevent duplicate fetch in React.StrictMode
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    try {
      setLoading(true);
      setError(null);
      const menu = await menuService.getMenu();
      setMenuItems(menu);
    } catch (err) {
      console.error('Failed to fetch menu:', err);
      setError('Failed to load menu');
      hasFetchedRef.current = false; // Allow retry on error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch menu when user is authenticated
    if (user) {
      fetchMenu();
    } else {
      // Reset state when user logs out
      setMenuItems([]);
      setError(null);
      hasFetchedRef.current = false;
    }
  }, [user]);

  return (
    <MenuContext.Provider
      value={{
        menuItems,
        loading,
        error,
        refetch: fetchMenu,
      }}
    >
      {children}
    </MenuContext.Provider>
  );
};

export const useMenu = (): MenuContextType => {
  const context = useContext(MenuContext);
  if (context === undefined) {
    throw new Error('useMenu must be used within a MenuProvider');
  }
  return context;
};
